import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
    LogOut,
    User,
    Menu,
    ChevronDown,
    Sun,
    Moon,
    Bell,
    Settings as SettingsIcon,
    CreditCard,
    Copy,
    Check,
} from "lucide-react";
import { clsx } from "clsx";
import { useAuth } from "@stores/auth";
import { useNotificationStore } from "@stores/notifications/notificationStore";
import { useTheme } from "@stores/theme/useTheme";
import { useToast } from "@hooks/useToast";
import { notificationsApi, type NotificationDto } from "@api/modules/notifications.api";
import { authApi } from "@api/modules/auth.api";
import { formatDateTime } from "@lib/formatters";
import { Avatar } from "@components/Avatar";
import { notificationTarget, notificationText } from "@lib/notificationLinks";
import { LanguageMenu } from "./LanguageMenu";

interface TenantHeaderProps {
    onMobileMenuToggle: () => void;
}

const menuItem =
    "mx-1 flex w-[calc(100%-0.5rem)] items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-dark transition-colors duration-200 hover:bg-neutral-light/70 dark:text-neutral-light dark:hover:bg-white/10";
const menuIcon = "flex-shrink-0 text-neutral-dark/60 dark:text-neutral-light/60";

export const TenantHeader: React.FC<TenantHeaderProps> = ({ onMobileMenuToggle }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, clearAuth, billingOnly } = useAuth();
    const { success: toastSuccess, error: toastError } = useToast();
    const unreadCount = useNotificationStore((s) => s.unreadCount);
    const setUnreadCount = useNotificationStore((s) => s.setUnreadCount);
    const { theme, toggleTheme } = useTheme();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showBellMenu, setShowBellMenu] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);
    const userMenuRef = useRef<HTMLDivElement>(null);
    const bellMenuRef = useRef<HTMLDivElement>(null);

    const preview = useQuery({
        queryKey: ["notifications", "preview", 5],
        queryFn: async () => {
            const res = await notificationsApi.list({ page: 1, perPage: 5 });
            setUnreadCount(res.unreadCount ?? 0);
            return res;
        },
        staleTime: 30_000,
        enabled: showBellMenu && !billingOnly,
    });
    const previewItems: NotificationDto[] = preview.data?.notifications ?? [];

    const tenantCode = user?.tenant?.tenantCode;
    const orgName = user?.companyName ?? user?.tenant?.companyName ?? user?.tenant?.name;
    const userName = user?.name || t("nav.header.userFallback");

    const handleLogout = () => {
        void authApi.logout().catch(() => {});
        clearAuth();
        navigate("/login");
    };

    const copyCode = async () => {
        if (!tenantCode) return;
        try {
            await navigator.clipboard.writeText(tenantCode);
            setCodeCopied(true);
            toastSuccess(t("nav.header.codeCopied"), {
                description: t("nav.header.codeCopiedDescription"),
            });
            window.setTimeout(() => setCodeCopied(false), 2000);
        } catch {
            toastError(t("nav.header.copyCode"), { description: t("nav.header.copyFailed") });
        }
    };

    useEffect(() => {
        if (!showUserMenu && !showBellMenu) return;
        const onDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (userMenuRef.current && !userMenuRef.current.contains(target)) setShowUserMenu(false);
            if (bellMenuRef.current && !bellMenuRef.current.contains(target)) setShowBellMenu(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setShowUserMenu(false);
                setShowBellMenu(false);
            }
        };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
        };
    }, [showUserMenu, showBellMenu]);

    const openNotification = (n: NotificationDto) => {
        setShowBellMenu(false);
        navigate(notificationTarget(n) ?? "/notifications");
    };

    return (
        <header className="sticky top-0 z-50 h-16 border-b border-neutral-border/50 bg-white/80 shadow-sm backdrop-blur-xl transition-all duration-300 dark:border-neutral-border/20 dark:bg-neutral-dark/80">
            <div className="flex h-full items-center justify-between px-4 lg:px-8">
                <div className="flex items-center gap-4">
                    <button
                        type="button"
                        onClick={onMobileMenuToggle}
                        aria-label={t("nav.sidebar.openMenu")}
                        className="-ms-2 rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5 lg:hidden"
                    >
                        <Menu size={22} className="text-neutral-dark dark:text-neutral-light" />
                    </button>
                    <div className="flex items-center gap-2">
                        <img
                            src="/maeiz-icon.png"
                            alt={t("common.brand")}
                            className="ms-2 h-14 w-auto lg:ms-4"
                        />
                        {orgName && (
                            <span className="hidden max-w-[220px] truncate font-display text-sm font-semibold text-neutral-dark/80 dark:text-white/90 md:inline">
                                {orgName}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative" ref={bellMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowBellMenu((open) => !open);
                                setShowUserMenu(false);
                            }}
                            className="relative rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                            aria-label={t("nav.header.notifications")}
                            aria-expanded={showBellMenu}
                        >
                            <Bell size={20} className="text-neutral-dark dark:text-neutral-light" />
                            {unreadCount > 0 && (
                                <span className="absolute -end-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-gold px-1 text-[11px] font-bold text-neutral-ink">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </button>
                        {showBellMenu && (
                            <div
                                className="absolute end-0 top-full z-[60] mt-3 w-[min(100vw-2rem,17.5rem)] rounded-xl border border-neutral-border/50 bg-white/95 py-2 shadow-xl shadow-black/10 backdrop-blur-xl dark:border-neutral-border/20 dark:bg-neutral-dark/95 dark:shadow-black/30 sm:w-80"
                                role="menu"
                            >
                                <div className="border-b border-neutral-border/30 px-3 pb-2 dark:border-neutral-border/10">
                                    <p className="text-sm font-semibold text-neutral-dark dark:text-white">
                                        {t("nav.header.notifications")}
                                    </p>
                                </div>
                                <div className="max-h-72 overflow-y-auto">
                                    {billingOnly ? (
                                        <p className="px-3 py-4 text-sm text-neutral-dark/60 dark:text-neutral-light/60">
                                            {t("nav.header.notificationsLocked")}
                                        </p>
                                    ) : preview.isLoading ? (
                                        <p className="px-3 py-4 text-sm text-neutral-dark/60 dark:text-neutral-light/60">
                                            {t("common.states.loading")}
                                        </p>
                                    ) : previewItems.length === 0 ? (
                                        <p className="px-3 py-4 text-sm text-neutral-dark/60 dark:text-neutral-light/60">
                                            {t("nav.header.noNotifications")}
                                        </p>
                                    ) : (
                                        previewItems.map((n) => {
                                            const text = notificationText(t, n);
                                            return (
                                                <button
                                                    key={n.id}
                                                    type="button"
                                                    onClick={() => openNotification(n)}
                                                    className="flex w-full gap-3 px-3 py-2.5 text-start transition-colors hover:bg-neutral-light/70 dark:hover:bg-white/10"
                                                    role="menuitem"
                                                >
                                                    <span
                                                        className={clsx(
                                                            "mt-1.5 h-2 w-2 flex-shrink-0 rounded-full",
                                                            n.readAt ? "bg-transparent" : "bg-tertiary"
                                                        )}
                                                        aria-hidden
                                                    />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm font-medium text-neutral-dark dark:text-white">
                                                            {text.title || t("nav.header.notificationFallback")}
                                                        </span>
                                                        <span className="mt-0.5 block text-xs text-neutral-dark/50 dark:text-neutral-light/50">
                                                            {formatDateTime(n.createdAt)}
                                                        </span>
                                                    </span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                {!billingOnly && (
                                    <div className="border-t border-neutral-border/30 px-3 pt-2 dark:border-neutral-border/10">
                                        <Link
                                            to="/notifications"
                                            onClick={() => setShowBellMenu(false)}
                                            className="text-sm font-medium text-secondary hover:underline"
                                        >
                                            {t("nav.header.viewAll")}
                                        </Link>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <LanguageMenu />
                    <button
                        type="button"
                        onClick={toggleTheme}
                        className="rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                        aria-label={t("nav.header.toggleTheme")}
                    >
                        {theme === "dark" ? (
                            <Sun size={20} className="text-neutral-dark dark:text-neutral-light" />
                        ) : (
                            <Moon size={20} className="text-neutral-dark dark:text-neutral-light" />
                        )}
                    </button>
                    <div className="relative overflow-visible" ref={userMenuRef}>
                        <button
                            type="button"
                            onClick={() => {
                                setShowUserMenu((open) => !open);
                                setShowBellMenu(false);
                            }}
                            className="flex items-center gap-2 rounded-xl border border-neutral-border/50 bg-gradient-to-r from-neutral-light to-neutral-light/50 px-2 py-1.5 pe-2.5 transition-all duration-200 hover:bg-black/[0.03] dark:border-neutral-border/20 dark:from-white/5 dark:to-white/[0.02] dark:hover:bg-white/[0.04]"
                            aria-expanded={showUserMenu}
                            aria-haspopup="menu"
                        >
                            <Avatar name={userName} src={user?.profilePicture} size="sm" />
                            <span className="hidden min-w-0 max-w-[160px] truncate text-start text-sm font-semibold text-neutral-dark dark:text-white sm:inline">
                                {userName}
                            </span>
                            <ChevronDown
                                size={14}
                                className={clsx(
                                    "flex-shrink-0 text-neutral-dark/40 transition-transform duration-200 dark:text-neutral-light/40",
                                    showUserMenu && "rotate-180"
                                )}
                            />
                        </button>
                        {showUserMenu && (
                            <div
                                className="absolute end-0 top-full z-[60] mt-2 w-[min(100vw-2rem,17.5rem)] rounded-xl border border-neutral-border/50 bg-white/95 py-2 shadow-xl shadow-black/10 backdrop-blur-xl dark:border-neutral-border/20 dark:bg-neutral-dark/95 dark:shadow-black/30 sm:w-64"
                                role="menu"
                            >
                                <div className="border-b border-neutral-border/30 px-3 pb-2.5 pt-1 dark:border-neutral-border/10">
                                    <p className="truncate text-sm font-semibold text-neutral-dark dark:text-white">
                                        {userName}
                                    </p>
                                    {user?.plan?.name && (
                                        <span className="mt-0.5 inline-block rounded-full bg-secondary/10 px-2 py-0.5 text-xs text-secondary dark:bg-secondary/20">
                                            {user.plan.name}
                                        </span>
                                    )}
                                    <p className="my-1 truncate text-xs text-neutral-dark/50 dark:text-neutral-light/50">
                                        {orgName || t("nav.header.role")}
                                    </p>
                                </div>
                                {tenantCode && (
                                    <div className="border-b border-neutral-border/30 px-3 py-2.5 dark:border-neutral-border/10">
                                        <p className="mb-2 text-[10px] font-medium uppercase tracking-wide text-neutral-dark/50 dark:text-neutral-light/50">
                                            {t("nav.header.organizationCode")}
                                        </p>
                                        <button
                                            type="button"
                                            onClick={copyCode}
                                            className="flex w-full items-center justify-between gap-2 rounded-lg border border-neutral-border/40 bg-neutral-light/60 px-2.5 py-2 text-start transition-colors hover:bg-neutral-light dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10"
                                            title={t("nav.header.copyCode")}
                                        >
                                            <span
                                                className="min-w-0 flex-1 truncate font-mono text-xs text-neutral-dark dark:text-neutral-light"
                                                dir="ltr"
                                            >
                                                {codeCopied ? t("common.actions.copied") : tenantCode}
                                            </span>
                                            {codeCopied ? (
                                                <Check size={14} className="flex-shrink-0 text-green-600 dark:text-green-400" />
                                            ) : (
                                                <Copy size={14} className="flex-shrink-0 text-secondary opacity-80" />
                                            )}
                                        </button>
                                    </div>
                                )}
                                <div className="py-1">
                                    <Link to="/profile" onClick={() => setShowUserMenu(false)} className={menuItem} role="menuitem">
                                        <User size={16} className={menuIcon} />
                                        <span className="whitespace-nowrap">{t("nav.profile")}</span>
                                    </Link>
                                    <Link to="/subscription" onClick={() => setShowUserMenu(false)} className={menuItem} role="menuitem">
                                        <CreditCard size={16} className={menuIcon} />
                                        <span className="whitespace-nowrap">{t("nav.subscription")}</span>
                                    </Link>
                                    {!billingOnly && (
                                        <Link to="/settings" onClick={() => setShowUserMenu(false)} className={menuItem} role="menuitem">
                                            <SettingsIcon size={16} className={menuIcon} />
                                            <span className="whitespace-nowrap">{t("nav.settings")}</span>
                                        </Link>
                                    )}
                                </div>
                                <div className="mx-1 border-t border-neutral-border/30 dark:border-neutral-border/10" />
                                <button type="button" onClick={handleLogout} className={menuItem} role="menuitem">
                                    <LogOut size={16} className={menuIcon} />
                                    <span className="whitespace-nowrap">{t("nav.header.signOut")}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};
